import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { EnvBadge } from "../components/EnvBadge";
import { getAppBuildInfo } from "../config/build-info";
import { getFeatureFlags } from "../config/features";
import { getAppEnvironment } from "../config/app-environment";
import { pageTitle } from "../config/site";
import { formatStatusDetail } from "../lib/format-status";
import { initWebGpuStage } from "../lib/webgpu-stage";
import { StatusCrashLog } from "./StatusCrashLog";
import { StatusReleases } from "./StatusReleases";

export function StatusPage() {
  const build = getAppBuildInfo();
  const buildJson = JSON.stringify(build, null, 2);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [statusDetail, setStatusDetail] = useState("Initializing WebGPU…");

  useEffect(() => {
    document.title = pageTitle("Status");
    const canvas = canvasRef.current;
    if (canvas === null) {
      return;
    }

    const featureFlags = getFeatureFlags(getAppEnvironment());
    let disposed = false;
    let cleanup: (() => void) | undefined;

    const setStatus = (detail: string): void => {
      if (!disposed) {
        setStatusDetail(formatStatusDetail(detail, featureFlags));
      }
    };

    initWebGpuStage(canvas, setStatus)
      .then((teardown) => {
        if (disposed) {
          teardown();
        } else {
          cleanup = teardown;
        }
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : String(error);
        setStatus(message);
      });

    return () => {
      disposed = true;
      cleanup?.();
    };
  }, []);

  return (
    <div className="status-page">
      <header className="status-toolbar">
        <Link className="btn btn-ghost" to="/">
          Home
        </Link>
        <EnvBadge />
      </header>
      <StatusCrashLog />
      <section className="status-deploy" aria-labelledby="status-deploy-title">
        <h1 id="status-deploy-title" className="status-deploy__title">
          Deploy status
        </h1>
        <dl className="status-deploy__list">
          <div className="status-deploy__row">
            <dt>Environment</dt>
            <dd>{build.environment}</dd>
          </div>
          <div className="status-deploy__row">
            <dt>Commit</dt>
            <dd>
              <code>{build.gitSha}</code>
            </dd>
          </div>
          <div className="status-deploy__row">
            <dt>Commit (short)</dt>
            <dd>
              <code>{build.gitShaShort}</code>
            </dd>
          </div>
        </dl>
        <pre className="status-deploy__json" aria-label="Deploy status JSON">
          {buildJson}
        </pre>
      </section>
      <StatusReleases />
      <section className="status-gpu" aria-labelledby="status-gpu-title">
        <h2 id="status-gpu-title" className="status-gpu__title">
          Graphics
        </h2>
        <canvas
          ref={canvasRef}
          id="stage"
          className="status-canvas"
          aria-label="Character timeline visualization"
        />
        <p className="status-detail">{statusDetail}</p>
      </section>
    </div>
  );
}
